import { WebSocketClient } from '../lib/websocket';
import {
  createInterviewResourceCleanupController,
  createInterviewWebSocketControllers,
  type InterviewWebSocketAvatarContext,
} from './interviewWebSocketControllerFactory';
import { registerInterviewWebSocketEvents } from './interviewWebSocketEventRegistrar';
import {
  createInterviewWebSocketUiBridge,
  installMobileUiReconnectHook,
  resetInterviewWebSocketUi,
} from './interviewWebSocketRuntimeBridge';
import type { SharedRefs, WebSocketCallbacks } from './interviewWebSocketTypes';

export interface InterviewWebSocketRuntimeEnvironment {
  createWebSocketClient: () => WebSocketClient;
  storage: Pick<Storage, 'getItem'>;
  mobileHookTarget: object;
  setInterval: (handler: () => void, timeoutMs: number) => number;
  clearInterval: (timerId: number) => void;
  createSessionId: () => string;
  getInstructionId: () => string | undefined;
  getUserAgent: () => string | undefined;
  isDocumentVisible: () => boolean;
  nowMs: () => number;
  nowDate: () => Date;
  debugLog: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
}

export interface StartInterviewWebSocketRuntimeInput {
  refs: SharedRefs;
  getCallbacks: () => WebSocketCallbacks;
  avatar: InterviewWebSocketAvatarContext;
  environment: InterviewWebSocketRuntimeEnvironment;
}

export interface InterviewWebSocketRuntimeHandle {
  started: Promise<boolean>;
  cleanup: () => void;
}

/** controller生成から接続開始・cleanupまで、面接WebSocket runtimeを一括して組み立てる。 */
export function startInterviewWebSocketRuntime(
  input: StartInterviewWebSocketRuntimeInput,
): InterviewWebSocketRuntimeHandle {
  const { refs, getCallbacks, avatar, environment } = input;
  const ui = createInterviewWebSocketUiBridge(getCallbacks);
  const wsClient = environment.createWebSocketClient();
  refs.wsClientRef.current = wsClient;

  const removeMobileReconnectHook = installMobileUiReconnectHook({
    storage: environment.storage,
    target: environment.mobileHookTarget,
    forceReconnect: (reason) => wsClient.forceReconnect(reason),
  });

  environment.debugLog('[Client] WebSocket client created, registering handlers...');
  const controllers = createInterviewWebSocketControllers({
    wsClient,
    refs,
    getCallbacks,
    ui,
    avatar,
    createSessionId: environment.createSessionId,
    getInstructionId: environment.getInstructionId,
    getUserAgent: environment.getUserAgent,
    isDocumentVisible: environment.isDocumentVisible,
    nowMs: environment.nowMs,
    nowDate: environment.nowDate,
    debugLog: environment.debugLog,
  });

  registerInterviewWebSocketEvents({
    wsClient,
    controllers,
    sessionIdRef: refs.sessionIdRef,
    debugLog: environment.debugLog,
    warn: environment.warn,
  });
  resetInterviewWebSocketUi(ui, getCallbacks);

  // 可視中の録音でSTTが進まない状態を検知する保険。判定ロジックはSTT controllerが持つ。
  const sttStallTimer = environment.setInterval(() => {
    controllers.sttMessages.checkForStall();
  }, 3_000);

  const resourceCleanup = createInterviewResourceCleanupController({
    wsClient,
    refs,
    getCallbacks,
    ui,
    clearStallTimer: () => environment.clearInterval(sttStallTimer),
    removeMobileReconnectHook,
  });

  return {
    started: controllers.connectionLifecycle.start(),
    cleanup: () => resourceCleanup.cleanup(),
  };
}

/** 実ブラウザのAPIをruntime environmentへ閉じ込める。 */
export function createBrowserInterviewWebSocketRuntimeEnvironment(): InterviewWebSocketRuntimeEnvironment {
  let storage: Pick<Storage, 'getItem'> = { getItem: () => null };
  try {
    storage = window.localStorage;
  } catch {
    // privacy設定等でlocalStorage getter自体が失敗しても面接接続は継続する。
  }

  const debugEnabled = () => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('wsDebug') === '1' || storage.getItem('dentalAvatarWsDebug') === '1';
    } catch {
      return false;
    }
  };

  return {
    createWebSocketClient: () => new WebSocketClient('/conversation'),
    storage,
    mobileHookTarget: window,
    setInterval: (handler, timeoutMs) => window.setInterval(handler, timeoutMs),
    clearInterval: (timerId) => window.clearInterval(timerId),
    createSessionId: () => `session-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
    getInstructionId: () => new URLSearchParams(window.location.search).get('instructionId') || undefined,
    getUserAgent: () => typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    isDocumentVisible: () => typeof document === 'undefined' || document.visibilityState === 'visible',
    nowMs: () => Date.now(),
    nowDate: () => new Date(),
    debugLog: (...args) => {
      if (debugEnabled()) console.log(...args);
    },
    warn: (...args) => console.warn(...args),
  };
}
